import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { webhookAPI } from '../services/api';
import toast from 'react-hot-toast';
import { Send } from 'lucide-react';

const Webhook: React.FC = () => {
  const [webhookData, setWebhookData] = useState({
    action: 'BUY',
    symbol: '',
    exchange: 'NSE',
    quantity: 1,
    order_type: 'MARKET',
    product: 'MIS',
    price: '',
    trigger_price: '',
    broker: 'kite',
  });

  const sendMutation = useMutation({
    mutationFn: webhookAPI.send,
    onSuccess: (res: any) => {
      toast.success('Webhook processed successfully');
      console.log('Result:', res.data);
    },
    onError: () => toast.error('Failed to process webhook'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const data: any = {
      action: webhookData.action,
      symbol: webhookData.symbol.toUpperCase(),
      exchange: webhookData.exchange,
      quantity: parseInt(String(webhookData.quantity)),
      order_type: webhookData.order_type,
      product: webhookData.product,
      broker: webhookData.broker,
    };

    if (webhookData.price) {
      data.price = parseFloat(webhookData.price);
    }

    if (webhookData.trigger_price) {
      data.trigger_price = parseFloat(webhookData.trigger_price);
    }

    sendMutation.mutate(data);
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-gray-900">Webhook Test</h1>

      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Broker *
              </label>
              <select
                value={webhookData.broker}
                onChange={(e) =>
                  setWebhookData({ ...webhookData, broker: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="kite">Kite (Zerodha)</option>
                <option value="fyers">Fyers</option>
                <option value="upstox">Upstox</option>
                <option value="alpaca">Alpaca</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Action *
              </label>
              <select
                value={webhookData.action}
                onChange={(e) =>
                  setWebhookData({ ...webhookData, action: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="BUY">BUY</option>
                <option value="SELL">SELL</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Symbol *
              </label>
              <input
                type="text"
                required
                value={webhookData.symbol}
                onChange={(e) =>
                  setWebhookData({
                    ...webhookData,
                    symbol: e.target.value.toUpperCase(),
                  })
                }
                placeholder="e.g., SBIN, RELIANCE"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Exchange *
              </label>
              <select
                value={webhookData.exchange}
                onChange={(e) =>
                  setWebhookData({ ...webhookData, exchange: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="NSE">NSE</option>
                <option value="BSE">BSE</option>
                <option value="NFO">NFO</option>
                <option value="CDS">CDS</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Quantity *
              </label>
              <input
                type="number"
                required
                min="1"
                value={webhookData.quantity}
                onChange={(e) =>
                  setWebhookData({
                    ...webhookData,
                    quantity: parseInt(e.target.value),
                  })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Order Type *
              </label>
              <select
                value={webhookData.order_type}
                onChange={(e) =>
                  setWebhookData({ ...webhookData, order_type: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="MARKET">MARKET</option>
                <option value="LIMIT">LIMIT</option>
                <option value="SL">SL</option>
                <option value="SL-M">SL-M</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Product *
              </label>
              <select
                value={webhookData.product}
                onChange={(e) =>
                  setWebhookData({ ...webhookData, product: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="MIS">MIS (Intraday)</option>
                <option value="CNC">CNC (Delivery)</option>
                <option value="NRML">NRML</option>
              </select>
            </div>

            {webhookData.order_type !== 'MARKET' &&
              webhookData.order_type !== 'SL-M' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Price
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={webhookData.price}
                    onChange={(e) =>
                      setWebhookData({ ...webhookData, price: e.target.value })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              )}

            {(webhookData.order_type === 'SL' ||
              webhookData.order_type === 'SL-M') && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Trigger Price
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={webhookData.trigger_price}
                  onChange={(e) =>
                    setWebhookData({
                      ...webhookData,
                      trigger_price: e.target.value,
                    })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={sendMutation.isPending}
            className="flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
          >
            <Send size={20} />
            {sendMutation.isPending ? 'Sending...' : 'Send Webhook'}
          </button>
        </form>

        {/* Webhook URL Info */}
        <div className="mt-8 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-semibold text-gray-900 mb-2">Webhook Endpoint</h3>
          <code className="text-sm bg-white px-3 py-2 rounded border border-gray-200 block">
            POST http://localhost:8000/api/v1/webhook/signal
          </code>
          <p className="text-sm text-gray-600 mt-2">
            Use this endpoint for TradingView alerts or external signals.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Webhook;
